import { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid,
  Divider, Avatar, Stack, Chip,
  CircularProgress, MenuItem, IconButton, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  InputAdornment, Tabs, Tab, Paper, Collapse, Autocomplete,
  Dialog, DialogContent, DialogActions, Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Settings as SettingsIcon, Save as SaveIcon, CloudUpload as UploadIcon,
  Delete as DeleteIcon, Edit as EditIcon, Business as OrgIcon, Image as ImageIcon,
  Tag as TagIcon, HowToReg as RegistrationIcon, Class as AcademicsIcon,
  AccountBalanceWallet as FinanceIcon, Badge as HrIcon,
  RoomPreferences as GeneralIcon,
  ConstructionOutlined as SoonIcon, AccountTree as AccountSetupIcon,
  Add as AddIcon, Search as SearchIcon, Person as IndividualIcon,
  Groups as ClassPaymentIcon, AccountBalance as OtherIncomeIcon,
  ExpandMore as ChevronDownIcon, ChevronRight as ChevronRightIcon,
  AccountBalance as BankIcon, AccountBalanceWallet as WalletIcon,
  Payments as ExpendituresIcon, Category as CategoryIcon,
  Bolt as BoltIcon, DirectionsCar as CarIcon, Restaurant as RestaurantIcon,
  Build as BuildIcon, BusinessCenter as OfficeIcon, Phone as PhoneIcon,
  LocalShipping as ShippingIcon, MenuBook as BookIcon, Healing as HealthIcon,
  GetApp as DownloadIcon, Clear as ClearIcon, FilterAlt as FilterIcon,
  EventNote as PeriodsIcon, Tune as TuneIcon, Close as CloseIcon,
  Check as CheckIcon, Cancel as CancelIcon,
  LocalOffer as PackageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import { AccountingPeriodsCard } from '../accounting/AccountingPeriodsPage';
import PackagesDialog from './PackagesDialog';
import SchoolWeekCard from './SchoolWeekCard';

// Accounting-style typography tokens. IBM Plex Mono is a financial-software
// staple (BBC, BBVA, Plaid use it); IBM Plex Sans is its companion sans.
const FONT_MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const FONT_ACCT = '"IBM Plex Sans", "Inter", "Roboto", sans-serif';
const TNUM = { fontVariantNumeric: 'tabular-nums' };

const CATEGORIES = [
  { key: 'general',      label: 'General',       icon: <GeneralIcon />,      desc: 'School information & branding' },
  { key: 'registration', label: 'Registration',  icon: <RegistrationIcon />, desc: 'Student ID prefix and admission setup' },
  { key: 'academics',    label: 'Academics',     icon: <AcademicsIcon />,    desc: 'Academic year, grading and exam settings' },
  { key: 'finance',      label: 'Finance',       icon: <FinanceIcon />,      desc: 'Fee items, accounts and accounting' },
  { key: 'hr',           label: 'HR',            icon: <HrIcon />,           desc: 'Staff and payroll settings' },
];

function SectionTitle({ icon, title, subtitle }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
      <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>{icon}</Avatar>
      <Box>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
    </Stack>
  );
}

function ComingSoon({ icon, title, hint }) {
  return (
    <Card sx={{ p: 5, textAlign: 'center' }}>
      <Avatar sx={{
        width: 64, height: 64, mx: 'auto', mb: 2,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: 'primary.main',
      }}>
        {icon || <SoonIcon sx={{ fontSize: 32 }} />}
      </Avatar>
      <Typography variant="h6" fontWeight={700} gutterBottom>{title || 'Coming soon'}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
        {hint || 'Settings for this category will appear here once defined.'}
      </Typography>
    </Card>
  );
}

// ── General — school info + logo + delete logo ────────────────────
function GeneralSettings({ form, setForm, saveDetails, saving, logo, setLogo, logoSaving, fileRef, handleLogo, removeLogo }) {
  return (
    <>
      <SectionTitle icon={<OrgIcon />} title="General"
        subtitle="The core information that identifies your school" />

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <ImageIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>School Logo</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{
                border: '2px dashed', borderColor: 'divider',
                borderRadius: 2, p: 3, textAlign: 'center', mb: 2,
                bgcolor: '#fafafa',
                minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {logo
                  ? <Box component="img" src={logo} alt="Logo" sx={{ maxWidth: '100%', maxHeight: 200 }} />
                  : <Stack alignItems="center" spacing={0.8}>
                      <ImageIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">No logo uploaded</Typography>
                    </Stack>}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="contained" startIcon={<UploadIcon />}
                  onClick={() => fileRef.current?.click()} disabled={logoSaving}>
                  {logoSaving ? 'Saving…' : (logo ? 'Replace' : 'Upload')}
                </Button>
                {logo && (
                  <Button color="error" variant="outlined" onClick={removeLogo} disabled={logoSaving}>
                    <DeleteIcon />
                  </Button>
                )}
              </Stack>
              <input ref={fileRef} type="file" accept="image/*" hidden
                onChange={(e) => handleLogo(e.target.files?.[0])} />
              <Typography variant="caption" color="text.disabled" display="block" mt={1.2}>
                PNG or JPG · max 2 MB
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <OrgIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>School Information</Typography>
              </Stack>
              <Divider sx={{ mb: 2.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="School Name" value={form.school_name}
                    onChange={(e) => setForm({ ...form, school_name: e.target.value })} required />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Motto" value={form.motto}
                    onChange={(e) => setForm({ ...form, motto: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Address" multiline rows={2} value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email" type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Region" value={form.region}
                    onChange={(e) => setForm({ ...form, region: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Established Year" value={form.established_year}
                    onChange={(e) => setForm({ ...form, established_year: e.target.value })}
                    inputProps={{ maxLength: 4 }} />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={saveDetails} disabled={saving}>
                    Save Changes
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

// ── Finance — Account Setup ──────────────────────────────────────
//
// Master-detail layout: form + summary on the left, live grouped list on the
// right so the user can see what already exists while adding a new account.

const CATEGORY_META = {
  'Individual Payment': { color: 'primary',   tone: '#1565C0', icon: <IndividualIcon />,    desc: 'Per-student account (e.g. School Fees, PTA Dues)' },
  'Class Payment':      { color: 'secondary', tone: '#0d47a1', icon: <ClassPaymentIcon />,  desc: 'Fee charged to a whole class' },
  'Other Income':       { color: 'success',   tone: '#2e7d32', icon: <OtherIncomeIcon />,   desc: 'General school income (rent, transport…)' },
};

function CategoryStatPill({ category, count }) {
  const m = CATEGORY_META[category];
  return (
    <Box sx={{
      flex: 1, minWidth: 0,
      p: 1.5, borderRadius: 2,
      bgcolor: (t) => alpha(m.tone, 0.08),
      border: '1px solid', borderColor: (t) => alpha(m.tone, 0.2),
    }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: m.tone }}>
        {m.icon}
        <Typography sx={{
          fontFamily: FONT_ACCT, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 9.5,
        }}>
          {category}
        </Typography>
      </Stack>
      <Typography sx={{
        fontFamily: FONT_MONO, fontWeight: 600, color: m.tone,
        mt: 0.4, lineHeight: 1.05, fontSize: 24, ...TNUM,
      }}>
        {count}
      </Typography>
    </Box>
  );
}

function AccountSetupCard({
  accounts, accLoading, accForm, setAccForm, accSaving, saveAccount, removeAccount, accSearch, setAccSearch,
}) {
  // Per-category fold state (persisted across category switches via component state)
  const [collapsed, setCollapsed] = useState(new Set());
  // Packages dialog — opens per row for income_types-sourced accounts.
  // Individual Payment accounts don't participate in daily collections,
  // so they don't get a packages button.
  const [pkgOpen, setPkgOpen]   = useState(false);
  const [pkgType, setPkgType]   = useState('');
  const toggleCat = (cat) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };
  const filtered = accSearch.trim()
    ? accounts.filter(a => `${a.account} ${a.category}`.toLowerCase().includes(accSearch.trim().toLowerCase()))
    : accounts;

  // Group by category for the right-pane list
  const grouped = {
    'Individual Payment': filtered.filter(a => a.category === 'Individual Payment'),
    'Class Payment':      filtered.filter(a => a.category === 'Class Payment'),
    'Other Income':       filtered.filter(a => a.category === 'Other Income'),
  };

  const totals = {
    'Individual Payment': accounts.filter(a => a.category === 'Individual Payment').length,
    'Class Payment':      accounts.filter(a => a.category === 'Class Payment').length,
    'Other Income':       accounts.filter(a => a.category === 'Other Income').length,
  };

  return (
    <Grid container spacing={2.5}>
      {/* ── LEFT: Add form + stats ─────────────────────────────────── */}
      <Grid item xs={12} md={5}>
        <Card sx={{ borderRadius: 3, mb: 2.5 }}>
          <Box sx={{
            px: 2.5, py: 1.8,
            borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
          }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{
                bgcolor: 'primary.main', width: 32, height: 32,
              }}><AddIcon sx={{ fontSize: 18 }} /></Avatar>
              <Box>
                <Typography sx={{
                  fontFamily: FONT_ACCT, fontWeight: 700, fontSize: 15,
                }}>Add New Account</Typography>
                <Typography variant="caption" color="text.secondary">
                  Define a payment account the school accepts
                </Typography>
              </Box>
            </Stack>
          </Box>
          <CardContent sx={{ p: 2.5 }}>
            <TextField select fullWidth label="Payment Category"
              value={accForm.category} onChange={(e) => setAccForm({ ...accForm, category: e.target.value })}
              sx={{ mb: 2 }}
              helperText={accForm.category && CATEGORY_META[accForm.category]?.desc}>
              <MenuItem value="">— Select category —</MenuItem>
              {Object.keys(CATEGORY_META).map(c => (
                <MenuItem key={c} value={c}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: CATEGORY_META[c].tone }}>
                    {CATEGORY_META[c].icon}
                    <span>{c}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            <TextField fullWidth label="Account Name"
              value={accForm.account}
              onChange={(e) => setAccForm({ ...accForm, account: e.target.value })}
              placeholder="e.g. School_Fees, PTA_Dues, Feeding"
              helperText="Spaces become underscores · letters and numbers only"
              sx={{ mb: 2.5 }} />

            <Button fullWidth size="large" variant="contained"
              startIcon={accSaving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              onClick={saveAccount}
              disabled={accSaving || !accForm.account.trim() || !accForm.category}>
              Save Account
            </Button>
          </CardContent>
        </Card>

        {/* Stats summary */}
        <Card sx={{ borderRadius: 3 }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{
              fontFamily: FONT_ACCT, fontWeight: 700, fontSize: 11,
              color: 'text.secondary', letterSpacing: 1.2, textTransform: 'uppercase',
            }}>
              At a glance
            </Typography>
          </Box>
          <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
            {Object.keys(CATEGORY_META).map(c => (
              <CategoryStatPill key={c} category={c} count={totals[c]} />
            ))}
          </Box>
        </Card>
      </Grid>

      {/* ── RIGHT: Live grouped list ────────────────────────────────── */}
      <Grid item xs={12} md={7}>
        <Card sx={{ borderRadius: 3, height: '100%' }}>
          <Box sx={{
            px: 2.5, py: 1.4,
            borderBottom: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 200 }}>
              <Avatar sx={{
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                color: 'primary.main', width: 32, height: 32,
              }}><AccountSetupIcon sx={{ fontSize: 18 }} /></Avatar>
              <Box>
                <Typography sx={{
                  fontFamily: FONT_ACCT, fontWeight: 700, fontSize: 15,
                }}>Defined Accounts</Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ fontFamily: FONT_ACCT, ...TNUM }}>
                  {accounts.length} account{accounts.length === 1 ? '' : 's'} · grouped by category
                </Typography>
              </Box>
            </Stack>
            <TextField size="small" placeholder="Search…" value={accSearch}
              onChange={(e) => setAccSearch(e.target.value)}
              sx={{ minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Box>

          <Box sx={{ p: 1.5, maxHeight: 580, overflowY: 'auto' }}>
            {accLoading ? (
              <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : accounts.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <AccountSetupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.disabled" gutterBottom>
                  No payment accounts defined yet
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Use the form on the left to add your first account
                </Typography>
              </Box>
            ) : Object.entries(grouped).map(([cat, list]) => {
              if (list.length === 0) return null;
              const m = CATEGORY_META[cat];
              const isFolded = collapsed.has(cat);
              return (
                <Box key={cat} sx={{ mb: 1.2 }}>
                  {/* Foldable header — click anywhere to toggle */}
                  <Stack direction="row" spacing={1} alignItems="center"
                    onClick={() => toggleCat(cat)}
                    sx={{
                      px: 1.5, py: 0.9, borderRadius: 1.5,
                      bgcolor: (t) => alpha(m.tone, 0.07),
                      border: '1px solid', borderColor: (t) => alpha(m.tone, 0.15),
                      cursor: 'pointer', userSelect: 'none',
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: (t) => alpha(m.tone, 0.12) },
                    }}>
                    <Box sx={{ color: m.tone, display: 'flex',
                      transition: 'transform 0.2s',
                      transform: isFolded ? 'rotate(0deg)' : 'rotate(0deg)',
                    }}>
                      {isFolded
                        ? <ChevronRightIcon sx={{ fontSize: 18, color: m.tone }} />
                        : <ChevronDownIcon  sx={{ fontSize: 18, color: m.tone }} />}
                    </Box>
                    <Box sx={{ color: m.tone, display: 'flex' }}>{m.icon}</Box>
                    <Typography sx={{
                      flex: 1, color: m.tone,
                      fontFamily: FONT_ACCT, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: 0.7, fontSize: 11.5,
                    }}>
                      {cat}
                    </Typography>
                    <Box sx={{
                      bgcolor: (t) => alpha(m.tone, 0.18), color: m.tone,
                      borderRadius: 10, px: 1, py: 0.15, minWidth: 28, textAlign: 'center',
                    }}>
                      <Typography sx={{
                        fontFamily: FONT_MONO, fontWeight: 600, fontSize: 11, ...TNUM,
                      }}>
                        {list.length}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Foldable body */}
                  <Collapse in={!isFolded} timeout="auto" unmountOnExit>
                    <Stack divider={<Divider />} sx={{ mt: 0.5, mx: 0.5 }}>
                      {list.map(a => (
                        <Stack key={`${a.source}-${a.id}-${a.account}`}
                          direction="row" alignItems="center" spacing={1}
                          sx={{
                            px: 1.5, py: 1.1,
                            borderRadius: 1,
                            transition: 'background 0.12s',
                            '&:hover': { bgcolor: (t) => alpha(m.tone, 0.05) },
                          }}>
                          <Box sx={{
                            width: 4, height: 22, borderRadius: 2,
                            bgcolor: (t) => alpha(m.tone, 0.4), flexShrink: 0,
                          }} />
                          <Typography sx={{
                            flex: 1, fontFamily: FONT_MONO, fontWeight: 500,
                            fontSize: 13.5, letterSpacing: 0.1, color: 'text.primary',
                            ...TNUM,
                          }}>
                            {a.account}
                          </Typography>
                          {a.source === 'income_types' && (
                            <Tooltip title="Manage packages (price tiers)">
                              <IconButton size="small" color="primary"
                                onClick={() => { setPkgType(a.account); setPkgOpen(true); }}>
                                <PackageIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => removeAccount(a)}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}

            {!accLoading && accounts.length > 0 && filtered.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                <Typography variant="body2" color="text.disabled">
                  No accounts match "{accSearch}"
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Grid>

      <PackagesDialog
        open={pkgOpen}
        incomeType={pkgType}
        onClose={() => setPkgOpen(false)} />
    </Grid>
  );
}

// ── Finance — Bank Accounts ──────────────────────────────────────
//
// Mirrors the legacy Add_acount.java flow. Every bank / cash account the
// school keeps money in is registered here with its current Balance Brought
// Forward (B/fwd). Saving an account also seeds the cash_book and cash_column
// tables so the rest of the accounting flow (deposits, expenses, monthly
// carry-forward, reports) has the right opening anchor for that account.
//
// What's in the legacy save (replicated faithfully):
//   • acc_name      — registry: na, bf, dat, plus optional acc_num & account_type
//   • cash_book     — bbf row in EVERY existing month (0 in past months,
//                     real opening balance in the current month) so reports
//                     across history stay consistent
//   • cash_column   — companion "Balance B/fwd-<name>" row per month
//
// Editing rules from legacy (preserved):
//   • Renaming an account propagates to cash_book and cash_column entries
// ── CashbookApprovalCard ────────────────────────────────────────
// Single toggle: should new Payment Vouchers be queued for approval
// before they hit the cashbook? Reads/writes via /api/cashbook/settings
// which is gated server-side to super_admin or `settings` page holders.
function CashbookApprovalCard() {
  const [flag, setFlag] = useState(false);
  const [levels, setLevels] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get('/cashbook/settings');
      setFlag(!!r.data?.expenditure_approval_required);
      setLevels(Number(r.data?.expenditure_approval_levels) || 3);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveLevels = async (next) => {
    setSaving(true);
    try {
      await API.put('/cashbook/settings', {
        expenditure_approval_required: flag,
        expenditure_approval_levels: next,
      });
      await load();
      swal.success(`Requests now collect ${next} signature${next === 1 ? '' : 's'}.`);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not change the number of signatures'));
    } finally { setSaving(false); }
  };

  const submit = async (next) => {
    const ok = await swal.confirm({
      title: next ? 'Turn ON expenditure approval?' : 'Turn OFF expenditure approval?',
      text: next
        ? 'Money will no longer be able to leave without an Expenditure Request that has '
          + 'been signed off first. The spender raises a short request with no cheque '
          + 'number; only once it is approved can a cheque be written against it. '
          + 'Vouchers already waiting for approval are unaffected.'
        : 'Payment Vouchers will post immediately, with no request and no separate approval step.',
      tone: next ? 'warning' : 'primary',
      confirmText: next ? 'Yes, require approval' : 'Yes, post immediately',
    });
    if (!ok) return;
    setSaving(true);
    try {
      // PUT then immediately re-GET so the displayed flag reflects
      // exactly what's in the database — any silent failure (403, DB
      // error) surfaces here because the GET would still return the
      // old value.
      await API.put('/cashbook/settings', { expenditure_approval_required: next });
      const verify = await API.get('/cashbook/settings');
      const stored = !!verify.data?.expenditure_approval_required;
      setFlag(stored);
      if (stored !== next) {
        swal.error(
          'Toggle did not save',
          'The database still shows the old value. You may not have permission to change this setting (only super_admin or users with the Settings page can).'
        );
      } else {
        swal.success(stored
          ? 'Approval mode is ON — money can no longer leave without an Expenditure Request that has been signed off first.'
          : 'Approval mode is OFF — Payment Vouchers post immediately, with no request needed.');
      }
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not change approval mode'));
    } finally { setSaving(false); }
  };

  return (
    <Card sx={{ borderRadius: 2 }}>
      <Box sx={{ px: 2.5, py: 1.6, borderBottom: 1, borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main' }}>
          <BookIcon fontSize="small" />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            Cashbook · Expenditure Approval
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Decide whether Payment Vouchers must be approved before they hit the cashbook and journal.
          </Typography>
        </Box>
      </Box>
      <CardContent>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={2}
              sx={{ p: 2, borderRadius: 2, bgcolor: flag ? 'warning.50' : 'success.50',
                border: '1px solid', borderColor: flag ? 'warning.light' : 'success.light' }}>
              <Avatar sx={{ bgcolor: flag ? 'warning.main' : 'success.main', width: 38, height: 38 }}>
                <BookIcon fontSize="small" />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={800}>
                  Approval mode is currently {flag ? 'ON' : 'OFF'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {flag
                    ? 'Nothing can be paid without an Expenditure Request that has been signed off. '
                      + 'The spender raises a short request with NO cheque number, it collects its '
                      + 'signatures, and only then can a cheque be written against it.'
                    : 'New Payment Vouchers post immediately. No request and no separate approval step.'}
                </Typography>
                <Typography variant="caption"
                  sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: 10.5,
                    color: flag ? 'warning.dark' : 'success.dark' }}>
                  Database value: school_setup.expenditure_approval_required = {flag ? 1 : 0}
                </Typography>
              </Box>
              <Button
                variant="contained" color={flag ? 'inherit' : 'warning'}
                disabled={saving}
                onClick={() => submit(!flag)}
                sx={{ fontWeight: 800, textTransform: 'none', minWidth: 180 }}>
                {saving ? '…' : (flag ? 'Turn OFF' : 'Turn ON approval')}
              </Button>
            </Stack>

            {/* How many of the three signature blocks on the printed
                Expenditure Approval Form this school actually collects.
                Shortens from the FRONT, so the Final Level signature is
                always the last one — a school with two levels drops the
                1st Level block, not the final one. */}
            {flag && (
              <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.3 }}>
                  How many signatures?
                </Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mb: 1.2 }}>
                  One is normal: somebody approves the request and the payment can go
                  through. The printed form has three signature blocks, so 2 and 3 are
                  there for a school that really uses them — unused blocks print blank
                  rather than repeating a name.
                </Typography>
                <TextField select size="small" value={levels} sx={{ minWidth: 380 }}
                  disabled={saving}
                  onChange={(e) => saveLevels(Number(e.target.value))}>
                  <MenuItem value={1}>1 — one approval, then it can be paid (normal)</MenuItem>
                  <MenuItem value={2}>2 — Department Head → Final Approval</MenuItem>
                  <MenuItem value={3}>3 — Department Head → 1st Level → Final Approval</MenuItem>
                </TextField>
              </Box>
            )}

            <Alert severity="info" variant="outlined" sx={{ mt: 2, borderRadius: 1.5 }}>
              <Typography variant="caption" fontWeight={700}>How it works:</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.4, fontSize: 12 }}>
                <li>On the Roles page, grant <b>Approve Expenditure Requests</b> to whoever
                    approves spending. That one right is the whole approval.</li>
                <li>The spender raises an <b>Expenditure Request</b> on the Cashbook —
                    a short form with <b>no cheque number</b>, because nobody knows it yet.</li>
                <li>The approver opens <b>To approve</b> and approves it. <b>Nobody can
                    approve their own request</b>, whatever rights they hold — that is the
                    point of the form.</li>
                <li>It then appears under <b>Ready to pay</b>. The spender clicks it, the
                    voucher opens prefilled and locked, and they add only the account, the
                    date and the <b>cheque number</b>. Posting writes the cashbook and
                    journal straight away — there is no second approval.</li>
                <li>A refusal needs a reason, and no money moves.</li>
                <li>An approved request funds <b>one</b> cheque — it cannot be paid twice.</li>
              </Box>
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
}

//   • Editing the opening balance updates the bbf row tagged
//     'Opening System Balance'
//   • An account that already has real (non-bbf) cash_book transactions
//     can't be deleted — they should be deactivated instead.
function BankAccountsCard() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);

  const blank = {
    name: '', acc_num: '', account_type: 'Bank',
    bf: '', dat: new Date().toISOString().slice(0, 10),
  };
  const [form, setForm]   = useState(blank);
  const [saving, setSav]  = useState(false);

  const load = async () => {
    setLoading(true);
    try { setAccounts((await API.get('/bank-accounts')).data); }
    catch { setAccounts([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim())  { swal.warning('Enter a bank account name'); return; }
    if (form.bf === '' || !Number.isFinite(Number(form.bf))) {
      swal.warning('Enter the current account balance');
      return;
    }
    const ok = await swal.confirm({
      title: 'Save bank account?',
      html: `<strong>${form.name}</strong><br/>` +
            `${form.account_type}${form.acc_num ? ' · ' + form.acc_num : ''}<br/>` +
            `Opening Balance: <strong>GH¢ ${Number(form.bf).toLocaleString('en-GH', { minimumFractionDigits: 2 })}</strong> ` +
            `as of <strong>${form.dat}</strong>` +
            `<br/><br/><small>Once saved, the opening balance enters the cash book ` +
            `and propagates through every existing month — please make sure the ` +
            `figure is correct.</small>`,
      confirmText: 'Yes, save account',
      icon: 'question',
      tone: 'primary',
    });
    if (!ok) return;

    setSav(true);
    try {
      await API.post('/bank-accounts', form);
      setForm(blank);
      swal.success(`Bank account "${form.name}" created`);
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Save failed')); }
    finally { setSav(false); }
  };

  const editName = async (a) => {
    const newName = await swal.prompt({
      title: 'Rename account',
      text: `Current name: ${a.name}`,
      inputLabel: 'New name',
      initialValue: a.name,
      confirmText: 'Save',
      validate: (v) => !v?.trim() && 'Name is required',
    });
    if (!newName || newName.trim() === a.name) return;
    try {
      await API.put(`/bank-accounts/${a.trans_code}/name`, { name: newName.trim() });
      swal.success(`Renamed to "${newName.trim()}"`);
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Rename failed')); }
  };

  const editBalance = async (a) => {
    const v = await swal.prompt({
      title: 'Update opening balance',
      html: `<strong>${a.name}</strong><br/><small>Current: GH¢ ${Number(a.bf || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })}</small>`,
      inputLabel: 'New balance (GH¢)',
      inputType: 'number',
      initialValue: String(a.bf ?? ''),
      confirmText: 'Update balance',
      validate: (val) => !Number.isFinite(Number(val)) && 'Enter a number',
    });
    if (v === null || v === '') return;
    if (Number(v) === Number(a.bf)) return;
    const ok = await swal.confirm({
      title: 'Confirm balance change',
      text: `Change "${a.name}" opening balance from ${Number(a.bf).toLocaleString('en-GH', { minimumFractionDigits: 2 })} to ${Number(v).toLocaleString('en-GH', { minimumFractionDigits: 2 })}?`,
      confirmText: 'Yes, update',
      tone: 'warning',
    });
    if (!ok) return;
    try {
      await API.put(`/bank-accounts/${a.trans_code}/balance`, { bf: Number(v) });
      swal.success('Opening balance updated');
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Update failed')); }
  };

  const remove = async (a) => {
    const ok = await swal.confirm({
      title: `Delete "${a.name}"?`,
      text: 'Only accounts with no real cash-book activity (deposits, expenses, etc.) can be deleted. Otherwise deactivate the account instead.',
      confirmText: 'Yes, delete',
      tone: 'danger',
      icon: 'warning',
    });
    if (!ok) return;
    try {
      await API.delete(`/bank-accounts/${a.trans_code}`);
      swal.success('Bank account deleted');
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const grandTotal = accounts.reduce((s, a) => s + Number(a.bf || 0), 0);
  const cashAccounts = accounts.filter(a => (a.account_type || '').toLowerCase() === 'cash');
  const hasCash = cashAccounts.length > 0;
  const cashTotal = cashAccounts.reduce((s, a) => s + Number(a.bf || 0), 0);
  const bankTotal = grandTotal - cashTotal;

  return (
    <Grid container spacing={2.5}>
      {/* ── LEFT: Add bank account ─────────────────────────────── */}
      <Grid item xs={12} md={5}>
        <Card sx={{ borderRadius: 3 }}>
          <Box sx={{
            px: 2.5, py: 1.8,
            borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
          }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <BankIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box>
                <Typography sx={{ fontFamily: FONT_ACCT, fontWeight: 700, fontSize: 15 }}>
                  Add Bank / Cash Account
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Bank, Cash-in-Hand, Mobile Money — register every place money lives
                </Typography>
              </Box>
            </Stack>
          </Box>
          <CardContent sx={{ p: 2.5 }}>
            {!hasCash && (
              <Box sx={{
                p: 1.5, mb: 2, borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                border: '1px solid', borderColor: (t) => alpha(t.palette.error.main, 0.3),
              }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Typography sx={{ fontSize: 18, lineHeight: 1, color: 'error.main' }}>!</Typography>
                  <Box>
                    <Typography sx={{ fontFamily: FONT_ACCT, fontWeight: 700, color: 'error.dark', fontSize: 12.5 }}>
                      Cash-in-Hand not set up
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'error.dark', display: 'block', mt: 0.3 }}>
                      Until at least one account with type <strong>Cash</strong> exists, the system
                      cannot accept any cash transactions (daily income, petty cash, deposits to bank).
                      Add a Cash account below to enable cash flow.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
            <TextField fullWidth label="Account Name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Adb-1131010109777, Cash, MTN_MoMo"
              helperText="Spaces become underscores · letters and numbers only"
              sx={{ mb: 2 }}
              required />

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={7}>
                <TextField fullWidth label="Account Number (optional)"
                  value={form.acc_num} onChange={(e) => setForm({ ...form, acc_num: e.target.value })}
                  placeholder="Bank account number" />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField select fullWidth label="Type"
                  value={form.account_type}
                  onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                  helperText={form.account_type === 'Cash'
                    ? 'Cash-in-Hand — required for cash transactions'
                    : ' '}>
                  <MenuItem value="Bank">Bank</MenuItem>
                  <MenuItem value="Cash">Cash (in-hand)</MenuItem>
                  <MenuItem value="Mobile Money">Mobile Money</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <TextField fullWidth type="date" label="As-of Date"
              InputLabelProps={{ shrink: true }}
              value={form.dat} onChange={(e) => setForm({ ...form, dat: e.target.value })}
              helperText="The date this opening balance applies from"
              sx={{ mb: 2 }} />

            <TextField fullWidth label="Account Balance" type="number"
              value={form.bf} onChange={(e) => setForm({ ...form, bf: e.target.value })}
              placeholder="0.00"
              InputProps={{
                startAdornment: <InputAdornment position="start">GH¢</InputAdornment>,
                sx: { fontFamily: FONT_MONO, ...TNUM, fontSize: 20, fontWeight: 600 },
              }}
              helperText="The current balance in this account at the as-of date"
              sx={{ mb: 2.5 }} />

            <Button fullWidth size="large" variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={submit} disabled={saving}>
              Save Account
            </Button>

            <Box sx={{
              mt: 2, p: 1.5, borderRadius: 1.5,
              bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
              border: '1px solid', borderColor: (t) => alpha(t.palette.warning.main, 0.25),
            }}>
              <Typography variant="caption" sx={{ fontFamily: FONT_ACCT, fontWeight: 600, color: 'warning.dark' }}>
                <strong>B/F note:</strong> Saving registers this account in the cash
                book with a Balance B/fwd row in every existing month, so reports
                across history stay consistent. Use type <strong>Cash</strong> for the
                physical money on hand at the start — the rest of the software (daily
                income, petty cash, deposits) checks for at least one Cash account
                before allowing cash transactions.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* ── RIGHT: Bank accounts list ──────────────────────────── */}
      <Grid item xs={12} md={7}>
        <Card sx={{ borderRadius: 3 }}>
          <Box sx={{ px: 2.5, py: 1.4, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main', width: 32, height: 32 }}>
                <WalletIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT_ACCT, fontWeight: 700, fontSize: 15 }}>
                  Registered Bank / Cash Accounts
                </Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ fontFamily: FONT_ACCT, ...TNUM }}>
                  {accounts.length} account{accounts.length === 1 ? '' : 's'} ·{' '}
                  {hasCash
                    ? `${cashAccounts.length} cash, ${accounts.length - cashAccounts.length} bank/other`
                    : 'Cash-in-Hand missing'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" sx={{
                    fontFamily: FONT_ACCT, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.6, fontSize: 9.5, display: 'block',
                  }}>
                    Bank
                  </Typography>
                  <Typography sx={{
                    fontFamily: FONT_MONO, fontWeight: 700, fontSize: 14,
                    color: 'primary.main', ...TNUM,
                  }}>
                    {bankTotal.toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" sx={{
                    fontFamily: FONT_ACCT, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.6, fontSize: 9.5, display: 'block',
                  }}>
                    Cash
                  </Typography>
                  <Typography sx={{
                    fontFamily: FONT_MONO, fontWeight: 700, fontSize: 14,
                    color: hasCash ? 'success.dark' : 'error.main', ...TNUM,
                  }}>
                    {cashTotal.toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right',
                  borderLeft: '1px solid', borderColor: 'divider', pl: 2,
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{
                    fontFamily: FONT_ACCT, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.6, fontSize: 9.5, display: 'block',
                  }}>
                    Total
                  </Typography>
                  <Typography sx={{
                    fontFamily: FONT_MONO, fontWeight: 700, fontSize: 16,
                    color: 'success.dark', ...TNUM,
                  }}>
                    GH¢ {grandTotal.toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ maxHeight: 620, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : accounts.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <BankIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.disabled" gutterBottom>
                  No bank or cash accounts registered yet
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Start with a <strong>Cash</strong> account so the system can accept cash transactions,
                  then add your bank accounts.
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                    color: 'text.secondary', fontFamily: FONT_ACCT, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5,
                  }}}>
                    <TableCell>Date</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Opening Balance</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map(a => (
                    <TableRow key={a.trans_code} hover>
                      <TableCell sx={{ fontFamily: FONT_MONO, fontSize: 12.5, color: 'text.secondary' }}>
                        {a.dat || '—'}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONT_MONO, fontWeight: 600, fontSize: 13.5 }}>
                          {a.name}
                        </Typography>
                        {a.acc_num && (
                          <Typography variant="caption" color="text.secondary"
                            sx={{ fontFamily: FONT_MONO, ...TNUM }}>
                            {a.acc_num}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const t = (a.account_type || 'Bank').toLowerCase();
                          const isCash = t === 'cash';
                          const isMomo = t === 'mobile money';
                          return (
                            <Chip size="small"
                              label={isCash ? 'Cash-in-Hand' : (a.account_type || 'Bank')}
                              variant="outlined"
                              color={isCash ? 'success' : isMomo ? 'warning' : 'primary'}
                              sx={{
                                fontFamily: FONT_ACCT, fontWeight: 700, fontSize: 10.5,
                                ...(isCash && { borderWidth: 2 }),
                              }} />
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{
                          fontFamily: FONT_MONO, fontWeight: 700, fontSize: 14,
                          color: 'success.dark', ...TNUM,
                        }}>
                          {Number(a.bf || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Rename account">
                          <IconButton size="small" color="primary" onClick={() => editName(a)}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit opening balance">
                          <IconButton size="small" onClick={() => editBalance(a)}>
                            <SaveIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => remove(a)}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
}

// ── Finance — Expenditures (categories + types) ────────────────
//
// Master setup screen used by payment & accounting modules. Lives in:
//   • exp_reg            — flat list of (category, expense_type) used by
//                          legacy peety / cash_book code (kept verbatim for
//                          backwards compat; soft-deleted via deleted_at)
//   • expense_category   — per-category metadata (icon + color) so the UI
//                          can show a colored, iconographic chip for each
//                          category without changing legacy lookups.
const EXP_ICONS = {
  Category:    <CategoryIcon />,
  Bolt:        <BoltIcon />,
  Car:         <CarIcon />,
  Restaurant:  <RestaurantIcon />,
  Build:       <BuildIcon />,
  Office:      <OfficeIcon />,
  Phone:       <PhoneIcon />,
  Shipping:    <ShippingIcon />,
  Book:        <BookIcon />,
  Health:      <HealthIcon />,
};
const EXP_COLORS = ['#1565C0', '#0d47a1', '#2e7d32', '#ed6c02', '#d32f2f',
                    '#6a1b9a', '#00838f', '#ad1457', '#5d4037', '#455a64'];

// ── Categories manager dialog ──────────────────────────────────
// Surfaced from a small icon button on the Expenditures table — gives a
// dedicated view for adding, renaming, recoloring and removing categories
// without having to attach an expense type first.
function CategoriesManagerDialog({ open, categories, onClose, onChanged }) {
  const [creating, setCreating] = useState({ name: '', icon: 'Category', color: EXP_COLORS[0] });
  const [editingName, setEditingName] = useState(null);  // original name
  const [draft, setDraft] = useState({ name: '', icon: '', color: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setCreating({ name: '', icon: 'Category', color: EXP_COLORS[0] });
      setEditingName(null);
      setError('');
    }
  }, [open]);

  const startEdit = (c) => {
    setEditingName(c.name);
    setDraft({ name: c.name, icon: c.icon || 'Category', color: c.color || EXP_COLORS[0] });
    setError('');
  };
  const cancelEdit = () => { setEditingName(null); setError(''); };

  const saveEdit = async () => {
    if (!draft.name.trim()) { setError('Category name cannot be empty'); return; }
    setBusy(true); setError('');
    try {
      await API.put(`/expenditures/categories/${encodeURIComponent(editingName)}`, {
        new_name: draft.name.trim(),
        icon: draft.icon,
        color: draft.color,
      });
      setEditingName(null);
      onChanged && (await onChanged());
    } catch (e) { setError(e?.response?.data?.message || e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  const create = async () => {
    if (!creating.name.trim()) { setError('Category name is required'); return; }
    setBusy(true); setError('');
    try {
      await API.post('/expenditures/categories', {
        name: creating.name.trim(),
        icon: creating.icon,
        color: creating.color,
      });
      setCreating({ name: '', icon: 'Category', color: EXP_COLORS[0] });
      onChanged && (await onChanged());
    } catch (e) { setError(e?.response?.data?.message || e.message || 'Create failed'); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (Number(c.usage_count) > 0) {
      setError(`"${c.name}" has historical petty-cash entries — cannot delete.`);
      return;
    }
    if (Number(c.type_count) > 0) {
      const ok = await swal.confirm({
        title: `Delete "${c.name}" with its ${c.type_count} type(s)?`,
        text: 'Every expense type under this category will be soft-deleted. Past records remain intact.',
        confirmText: 'Yes, delete all',
        tone: 'danger',
      });
      if (!ok) return;
    } else {
      const ok = await swal.confirmDelete(`category "${c.name}"`);
      if (!ok) return;
    }
    setBusy(true); setError('');
    try {
      await API.delete(`/expenditures/categories/${encodeURIComponent(c.name)}`, {
        params: Number(c.type_count) > 0 ? { force: 1 } : {},
      });
      onChanged && (await onChanged());
    } catch (e) { setError(e?.response?.data?.message || e.message || 'Delete failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}><CategoryIcon /></Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>Manage categories</Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Add, rename, recolour or remove the categories that group your expense types
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Existing list */}
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
          <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" fontWeight={800}>Existing categories</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip size="small" variant="outlined" label={`${categories.length}`} sx={{ fontWeight: 700 }} />
            </Stack>
          </Box>
          {categories.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">No categories yet — create one below.</Typography>
            </Box>
          ) : (
            <Box>
              {categories.map(c => {
                const isEditing = editingName === c.name;
                return (
                  <Box key={c.name} sx={{
                    px: 2, py: 1.2, borderTop: 1, borderColor: 'divider',
                    '&:first-of-type': { borderTop: 0 },
                  }}>
                    {!isEditing ? (
                      <Stack direction="row" alignItems="center" spacing={1.2}>
                        <Box sx={{ color: c.color, display: 'flex' }}>
                          {EXP_ICONS[c.icon] || EXP_ICONS.Category}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={700} sx={{ lineHeight: 1.15 }}>{c.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.type_count} type{c.type_count === 1 ? '' : 's'}
                            {Number(c.usage_count) > 0 && ` · ${c.usage_count} petty-cash use${c.usage_count === 1 ? '' : 's'}`}
                          </Typography>
                        </Box>
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => startEdit(c)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => remove(c)} disabled={busy}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Stack spacing={1.2}>
                        <TextField fullWidth size="small" autoFocus
                          label="Category name"
                          value={draft.name}
                          onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                          {Object.keys(EXP_ICONS).map(key => (
                            <Tooltip key={key} title={key}>
                              <IconButton size="small"
                                onClick={() => setDraft({ ...draft, icon: key })}
                                sx={{
                                  border: '1px solid',
                                  borderColor: draft.icon === key ? draft.color : 'divider',
                                  bgcolor: draft.icon === key ? alpha(draft.color, 0.12) : 'transparent',
                                  color: draft.icon === key ? draft.color : 'text.secondary',
                                  borderRadius: 1.5,
                                }}>
                                {EXP_ICONS[key]}
                              </IconButton>
                            </Tooltip>
                          ))}
                        </Box>
                        <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                          {EXP_COLORS.map(col => (
                            <Box key={col}
                              onClick={() => setDraft({ ...draft, color: col })}
                              sx={{
                                width: 26, height: 26, borderRadius: '50%',
                                bgcolor: col, cursor: 'pointer',
                                border: draft.color === col ? '3px solid #fff' : '2px solid transparent',
                                boxShadow: draft.color === col ? `0 0 0 2px ${col}` : 'none',
                              }} />
                          ))}
                        </Stack>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={cancelEdit} startIcon={<CancelIcon fontSize="small" />}>
                            Cancel
                          </Button>
                          <Button size="small" variant="contained" color="primary" onClick={saveEdit}
                            disabled={busy}
                            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckIcon fontSize="small" />}>
                            Save
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Card>

        {/* Add new category */}
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" fontWeight={800}>
              <AddIcon sx={{ fontSize: 16, mr: 0.6, verticalAlign: 'text-bottom' }} /> New category
            </Typography>
          </Box>
          <CardContent sx={{ pb: '16px !important' }}>
            <TextField fullWidth size="small" label="Category name" required
              placeholder="e.g. Capital Expenditure"
              value={creating.name}
              onChange={(e) => setCreating({ ...creating, name: e.target.value })}
              sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1.2 }}>
              {Object.keys(EXP_ICONS).map(key => (
                <Tooltip key={key} title={key}>
                  <IconButton size="small"
                    onClick={() => setCreating({ ...creating, icon: key })}
                    sx={{
                      border: '1px solid',
                      borderColor: creating.icon === key ? creating.color : 'divider',
                      bgcolor: creating.icon === key ? alpha(creating.color, 0.12) : 'transparent',
                      color: creating.icon === key ? creating.color : 'text.secondary',
                      borderRadius: 1.5,
                    }}>
                    {EXP_ICONS[key]}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
            <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
              {EXP_COLORS.map(col => (
                <Box key={col}
                  onClick={() => setCreating({ ...creating, color: col })}
                  sx={{
                    width: 26, height: 26, borderRadius: '50%',
                    bgcolor: col, cursor: 'pointer',
                    border: creating.color === col ? '3px solid #fff' : '2px solid transparent',
                    boxShadow: creating.color === col ? `0 0 0 2px ${col}` : 'none',
                  }} />
              ))}
            </Stack>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.7 }}>
        <Button onClick={onClose} color="inherit">Done</Button>
        <Button variant="contained" color="primary" onClick={create}
          disabled={busy || !creating.name.trim()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          sx={{ fontWeight: 800, minWidth: 160, textTransform: 'none' }}>
          Create category
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ExpendituresCard() {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [categories, setCats]   = useState([]);
  const [loading, setLoad]      = useState(true);

  // form
  const [editing, setEditing]   = useState(null);
  const blank = { cat: '', name: '', icon: 'Category', color: EXP_COLORS[0] };
  const [form, setForm]         = useState(blank);
  const [saving, setSaving]     = useState(false);

  // filters / view
  const [search, setSearch]     = useState('');
  const [filterCat, setFCat]    = useState('');
  const [perPage, setPP]        = useState('all');
  const [page, setPage]         = useState(1);

  // Categories manager dialog
  const [catMgr, setCatMgr]     = useState(false);
  // Per-category expand/collapse state in the table
  const [openCats, setOpenCats] = useState(new Set());
  const toggleCat = (name) => setOpenCats(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const load = async () => {
    setLoad(true);
    try {
      const params = { q: search, cat: filterCat, page, perPage };
      const [list, cats] = await Promise.all([
        API.get('/expenditures', { params }),
        API.get('/expenditures/categories'),
      ]);
      setRows(list.data.rows || []);
      setTotal(list.data.total || 0);
      setCats(cats.data || []);
    } catch (e) { /* ignore */ }
    finally { setLoad(false); }
  };
  useEffect(() => { setPage(1); }, [search, filterCat, perPage]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [search, filterCat, page, perPage]);

  // When user picks an existing category, auto-fill its icon/color
  useEffect(() => {
    if (!form.cat) return;
    const m = categories.find(c => c.name === form.cat);
    if (m) setForm((f) => ({ ...f, icon: m.icon || f.icon, color: m.color || f.color }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cat, categories]);

  const save = async () => {
    if (!form.cat.trim())  { swal.warning('Pick or type a category'); return; }
    if (!form.name.trim()) { swal.warning('Enter the expense type'); return; }
    setSaving(true);
    try {
      if (editing) {
        await API.put(`/expenditures/${editing.id}`, form);
        // Push icon/color to category metadata so other types in the same
        // category inherit the look
        await API.put(`/expenditures/categories/${encodeURIComponent(form.cat)}`,
          { icon: form.icon, color: form.color });
        swal.success('Expense type updated');
        setEditing(null);
        setForm(blank);
      } else {
        await API.post('/expenditures', form);
        // Keep category for serial entry of more types under the same category
        setForm((f) => ({ ...blank, cat: f.cat, icon: f.icon, color: f.color }));
        swal.success('Saved — add another or clear');
      }
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (r.usage_count > 0) {
      const ok = await swal.confirm({
        title: `"${r.name}" is in use`,
        html: `This expense type has been used <strong>${r.usage_count}</strong> time(s) in petty cash. ` +
              `Removing it hides it from new entries but keeps the historical records intact.`,
        confirmText: 'Yes, hide it',
        tone: 'warning',
      });
      if (!ok) return;
    } else {
      const ok = await swal.confirmDelete(`expense type "${r.name}"`);
      if (!ok) return;
    }
    try {
      await API.delete(`/expenditures/${r.id}`);
      swal.success('Removed');
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const editRow = (r) => {
    setEditing(r);
    setForm({ cat: r.cat, name: r.name, icon: r.icon || 'Category', color: r.color || EXP_COLORS[0] });
  };

  const clearForm = () => { setEditing(null); setForm(blank); };

  const exportCsv = () => {
    const a = document.createElement('a');
    a.href = '/api/expenditures/export.csv';
    a.download = `expenditures-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  // Build the canonical list of category groups for the expandable table.
  // Source of truth = the `categories` list (so empty categories still
  // appear and can be expanded to add the first type). The rows are
  // bucketed onto each category; if filtered, empty groups are dropped.
  const groupedRows = useMemo(() => {
    const map = new Map();
    for (const c of categories) {
      map.set(c.name, {
        name: c.name,
        icon: c.icon || 'Category',
        color: c.color || EXP_COLORS[0],
        type_count: Number(c.type_count || 0),
        usage_count: Number(c.usage_count || 0),
        items: [],
      });
    }
    for (const r of rows) {
      let g = map.get(r.cat);
      if (!g) {
        g = { name: r.cat, icon: r.icon, color: r.color, type_count: 0, usage_count: 0, items: [] };
        map.set(r.cat, g);
      }
      g.items.push(r);
    }
    let out = Array.from(map.values());
    // If a filter or search is in play, hide categories that have no
    // matching rows AND don't match the category filter directly.
    if (filterCat) out = out.filter(g => g.name === filterCat);
    if (search) out = out.filter(g => g.items.length > 0 || g.name.toLowerCase().includes(search.toLowerCase()));
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, categories, filterCat, search]);

  const totalCategories = categories.length;
  const totalUsage = rows.reduce((s, r) => s + Number(r.usage_count || 0), 0);

  return (
    <Grid container spacing={2.5}>
      {/* ── LEFT: form ──────────────────────────────────────── */}
      <Grid item xs={12} md={5}>
        <Card sx={{ borderRadius: 3 }}>
          <Box sx={{
            px: 2.5, py: 1.8, borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
          }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: editing ? 'warning.main' : 'primary.main', width: 32, height: 32 }}>
                <ExpendituresIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT_ACCT, fontWeight: 700, fontSize: 15 }}>
                  {editing ? `Edit · ${editing.name}` : 'New Expense Type'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Define the categories and types your accounting will charge against
                </Typography>
              </Box>
              {editing && <Chip size="small" label="Editing" color="warning" />}
            </Stack>
          </Box>

          <CardContent sx={{ p: 2.5 }}>
            <Autocomplete
              freeSolo
              // Blanks filtered out: MUI's default getOptionLabel reads
              // `option.label`, so one null row throws while the list is
              // opening, and a render throw blanks the whole page.
              options={[...new Set((categories || []).map(c => c?.name).filter(Boolean))]}
              getOptionLabel={(o) => String(o ?? '')}
              value={form.cat}
              onChange={(_, v) => setForm({ ...form, cat: v || '' })}
              onInputChange={(_, v) => setForm((f) => ({ ...f, cat: v || '' }))}
              renderInput={(params) => (
                <TextField {...params} label="Category" required
                  placeholder="Pick existing or type a new one"
                  helperText="Typing a new name auto-creates it" />
              )}
              renderOption={(props, opt) => {
                const m = categories.find(c => c.name === opt);
                return (
                  <li {...props} key={opt}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Box sx={{ color: m?.color, display: 'flex' }}>
                        {EXP_ICONS[m?.icon] || EXP_ICONS.Category}
                      </Box>
                      <Typography variant="body2" sx={{ flex: 1 }}>{opt}</Typography>
                      <Chip size="small" label={`${m?.type_count || 0} type(s)`}
                        sx={{ height: 18, fontSize: 10 }} />
                    </Stack>
                  </li>
                );
              }}
              sx={{ mb: 2 }}
            />

            <TextField fullWidth label="Expense Type" required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Plumbing, Lunch, Stationery"
              sx={{ mb: 2 }} />

            <Typography variant="caption" sx={{
              fontFamily: FONT_ACCT, fontWeight: 700, color: 'text.secondary',
              textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10, display: 'block', mb: 1,
            }}>
              Category Icon
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 2 }}>
              {Object.keys(EXP_ICONS).map(key => {
                const isActive = form.icon === key;
                return (
                  <Tooltip key={key} title={key}>
                    <IconButton size="small"
                      onClick={() => setForm({ ...form, icon: key })}
                      sx={{
                        border: '1px solid',
                        borderColor: isActive ? form.color : 'divider',
                        bgcolor: isActive ? alpha(form.color, 0.1) : 'transparent',
                        color: isActive ? form.color : 'text.secondary',
                        borderWidth: isActive ? 2 : 1,
                        borderRadius: 1.5,
                      }}>
                      {EXP_ICONS[key]}
                    </IconButton>
                  </Tooltip>
                );
              })}
            </Box>

            <Typography variant="caption" sx={{
              fontFamily: FONT_ACCT, fontWeight: 700, color: 'text.secondary',
              textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10, display: 'block', mb: 1,
            }}>
              Category Color
            </Typography>
            <Stack direction="row" spacing={0.6} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
              {EXP_COLORS.map(c => (
                <Box key={c}
                  onClick={() => setForm({ ...form, color: c })}
                  sx={{
                    width: 28, height: 28, borderRadius: '50%',
                    bgcolor: c, cursor: 'pointer',
                    border: form.color === c ? '3px solid #fff' : '2px solid transparent',
                    boxShadow: form.color === c ? `0 0 0 2px ${c}` : 'none',
                    transition: 'transform 0.12s',
                    '&:hover': { transform: 'scale(1.1)' },
                  }} />
              ))}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button fullWidth size="large" variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={save} disabled={saving}>
                {editing ? 'Save Changes' : 'Save'}
              </Button>
              <Button size="large" variant="outlined" startIcon={<ClearIcon />} onClick={clearForm}>
                Clear
              </Button>
            </Stack>

            {/* Live preview chip */}
            {(form.cat || form.name) && (
              <Box sx={{
                mt: 2.5, p: 1.5, borderRadius: 1.5,
                border: '1px dashed', borderColor: 'divider', bgcolor: 'background.default',
              }}>
                <Typography variant="caption" sx={{
                  fontFamily: FONT_ACCT, fontWeight: 700, color: 'text.disabled',
                  textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 9.5,
                }}>
                  Preview
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                  <Chip
                    icon={<Box sx={{ display: 'flex', color: form.color, ml: 0.6 }}>{EXP_ICONS[form.icon]}</Box>}
                    label={form.cat || 'Category'}
                    sx={{
                      bgcolor: alpha(form.color, 0.12), color: form.color,
                      borderColor: form.color, border: '1px solid',
                      fontFamily: FONT_ACCT, fontWeight: 700,
                    }} />
                  <Typography sx={{ fontFamily: FONT_MONO, fontWeight: 600 }}>
                    {form.name || 'Expense Type'}
                  </Typography>
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* ── RIGHT: table ────────────────────────────────────── */}
      <Grid item xs={12} md={7}>
        <Card sx={{ borderRadius: 3 }}>
          <Box sx={{ px: 2.5, py: 1.4, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
              <Avatar sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main', width: 32, height: 32 }}>
                <ExpendituresIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT_ACCT, fontWeight: 700, fontSize: 15 }}>
                  Expense Master
                </Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ fontFamily: FONT_ACCT, ...TNUM }}>
                  {total} type{total === 1 ? '' : 's'} · {totalCategories} categor{totalCategories === 1 ? 'y' : 'ies'} · {totalUsage} use{totalUsage === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Tooltip title="Export as CSV">
                <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>
                  Export
                </Button>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField size="small" placeholder="Search…" value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 200, flex: 1 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              <TextField select size="small" label="Category"
                value={filterCat} onChange={(e) => setFCat(e.target.value)}
                sx={{ minWidth: 180 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><FilterIcon fontSize="small" /></InputAdornment> }}>
                <MenuItem value="">All categories</MenuItem>
                {categories.map(c => (
                  <MenuItem key={c.name} value={c.name}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ color: c.color, display: 'flex' }}>{EXP_ICONS[c.icon] || EXP_ICONS.Category}</Box>
                      <span>{c.name}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title="Manage categories">
                <IconButton size="small" onClick={() => setCatMgr(true)}
                  sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                    color: 'primary.main',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.14) },
                  }}>
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={openCats.size === groupedRows.length ? 'Collapse all' : 'Expand all'}>
                <IconButton size="small"
                  onClick={() => setOpenCats(
                    openCats.size === groupedRows.length
                      ? new Set()
                      : new Set(groupedRows.map(g => g.name))
                  )}
                  sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                  }}>
                  {openCats.size === groupedRows.length ? <ChevronDownIcon /> : <ChevronRightIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Box sx={{ maxHeight: 720, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : groupedRows.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <ExpendituresIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.disabled">
                  {search || filterCat ? 'No categories match your filters' : 'No categories yet — open Manage categories to add one'}
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                    color: 'text.secondary', fontFamily: FONT_ACCT, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10.5,
                  }}}>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>Category</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>Types</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>Petty Cash Uses</TableCell>
                    <TableCell align="right" sx={{ width: 80 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedRows.map(g => {
                    const open = openCats.has(g.name);
                    return [
                      <TableRow key={`cat-${g.name}`} hover
                        sx={{ cursor: 'pointer',
                          bgcolor: open ? (t) => alpha(g.color, 0.07) : 'transparent',
                          '&:hover': { bgcolor: (t) => alpha(g.color, 0.10) },
                        }}
                        onClick={() => toggleCat(g.name)}>
                        <TableCell>
                          <IconButton size="small" sx={{ p: 0.4 }}>
                            {open ? <ChevronDownIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <Box sx={{
                              color: g.color, display: 'flex',
                              p: 0.6, borderRadius: 1,
                              bgcolor: (t) => alpha(g.color, 0.12),
                            }}>
                              {EXP_ICONS[g.icon] || EXP_ICONS.Category}
                            </Box>
                            <Typography sx={{
                              fontFamily: FONT_ACCT, fontWeight: 800, color: g.color,
                              fontSize: 13,
                            }}>
                              {g.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={`${g.items.length}`}
                            sx={{
                              bgcolor: alpha(g.color, 0.16), color: g.color,
                              fontFamily: FONT_MONO, ...TNUM, fontWeight: 800, fontSize: 11, height: 22,
                            }} />
                        </TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={g.usage_count} variant="outlined"
                            color={g.usage_count > 0 ? 'success' : 'default'}
                            sx={{ fontFamily: FONT_MONO, ...TNUM, fontWeight: 700, fontSize: 10.5, height: 20 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Add expense type to this category">
                            <IconButton size="small" color="primary"
                              onClick={(e) => { e.stopPropagation();
                                setForm({ cat: g.name, name: '', icon: g.icon, color: g.color });
                                setEditing(null);
                              }}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>,
                      <TableRow key={`expand-${g.name}`}>
                        <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ bgcolor: 'background.default', px: 0, py: 0 }}>
                              {g.items.length === 0 ? (
                                <Box sx={{ px: 4, py: 2.4, textAlign: 'center', color: 'text.secondary' }}>
                                  <Typography variant="caption">
                                    No expense types under <b>{g.name}</b> yet.
                                    <Button size="small" variant="text" sx={{ ml: 1, fontWeight: 700, textTransform: 'none' }}
                                      onClick={() => {
                                        setForm({ cat: g.name, name: '', icon: g.icon, color: g.color });
                                        setEditing(null);
                                      }}>
                                      Add the first one
                                    </Button>
                                  </Typography>
                                </Box>
                              ) : (
                                <Table size="small">
                                  <TableBody>
                                    {g.items.map(r => (
                                      <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}
                                        onDoubleClick={() => editRow(r)}>
                                        <TableCell sx={{ width: 40, borderBottom: 'none' }} />
                                        <TableCell sx={{ pl: 4, fontFamily: FONT_MONO, fontWeight: 600,
                                          borderBottomColor: 'divider' }}>
                                          <Stack direction="row" spacing={1} alignItems="center">
                                            <Box sx={{
                                              width: 6, height: 6, borderRadius: '50%',
                                              bgcolor: g.color,
                                            }} />
                                            <span>{r.name}</span>
                                          </Stack>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: 110, borderBottomColor: 'divider' }} />
                                        <TableCell align="right" sx={{ width: 110, borderBottomColor: 'divider' }}>
                                          <Chip size="small" label={r.usage_count}
                                            variant="outlined"
                                            color={r.usage_count > 0 ? 'success' : 'default'}
                                            sx={{ fontFamily: FONT_MONO, ...TNUM, fontWeight: 700, fontSize: 10.5, height: 20 }} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: 80, borderBottomColor: 'divider' }}>
                                          <Tooltip title="Edit">
                                            <IconButton size="small" color="primary" onClick={() => editRow(r)}>
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete">
                                            <IconButton size="small" color="error" onClick={() => remove(r)}>
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>,
                    ];
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        </Card>
      </Grid>

      <CategoriesManagerDialog open={catMgr}
        categories={categories}
        onClose={() => setCatMgr(false)}
        onChanged={load} />
    </Grid>
  );
}

// ── Registration — student ID prefix ─────────────────────────────
function RegistrationSettings({ prefix, setPrefix, savePrefix, prefixSaving }) {
  return (
    <>
      <SectionTitle icon={<RegistrationIcon />} title="Registration"
        subtitle="Settings that affect how students are registered into the system" />

      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
            <TagIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>Student ID Prefix</Typography>
          </Stack>
          <Divider sx={{ mb: 2.5 }} />
          <Typography variant="body2" color="text.secondary" mb={2}>
            Each newly registered student gets an index number in the format
            <strong> PREFIX_NUMBER</strong>, where the number is auto-generated and
            collision-free even when several admissions happen at the same time.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Prefix" value={prefix}
                onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
                helperText="Letters / numbers only, max 10" />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Box sx={{
                p: 1.5, borderRadius: 1.5, bgcolor: 'background.default',
                border: '1px dashed', borderColor: 'divider',
              }}>
                <Typography variant="caption" color="text.secondary" sx={{
                  display: 'block', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: 0.6, fontSize: 10,
                }}>
                  Preview
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {prefix || 'PREFIX'}_<span style={{ color: '#888' }}>123</span>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button fullWidth variant="contained" disabled={prefixSaving || !prefix.trim()}
                startIcon={prefixSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={savePrefix}>
                Save Prefix
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function SettingsPage() {
  const { refreshSchoolName } = useAuth();
  const fileRef = useRef(null);

  const [active, setActive]   = useState('general');
  const [info, setInfo]       = useState(null);
  const [form, setForm]       = useState({
    school_name: '', motto: '', address: '', phone: '', email: '',
    region: '', established_year: '',
  });
  const [logo, setLogo]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);
  const [prefix, setPrefix]   = useState('');
  const [prefixSaving, setPrefixSaving] = useState(false);

  // Account Setup (Finance)
  const [accounts, setAccounts] = useState([]);
  const [accLoading, setAccLoading] = useState(true);
  const [accForm, setAccForm] = useState({ account: '', category: '' });
  const [accSaving, setAccSaving] = useState(false);
  const [accSearch, setAccSearch] = useState('');
  const [financeTab, setFinanceTab] = useState('accounts');

  const load = async () => {
    try {
      const res = await API.get('/setup/info');
      const i   = res.data?.info || {};
      setInfo(i);
      setForm({
        school_name:      i.school_name      || '',
        motto:            i.motto            || '',
        address:          i.address          || '',
        phone:            i.phone            || '',
        email:            i.email            || '',
        region:           i.region           || '',
        established_year: i.established_year || '',
      });
      setLogo(i.logo || '');
    } catch { swal.error('Failed to load settings'); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    API.get('/setup/prefix').then(r => setPrefix(r.data?.prefix || '')).catch(() => {});
  }, []);

  const loadAccounts = async () => {
    setAccLoading(true);
    try { setAccounts((await API.get('/account-setup')).data); }
    catch { /* ignore */ }
    finally { setAccLoading(false); }
  };
  useEffect(() => { loadAccounts(); }, []);

  const saveAccount = async () => {
    if (!accForm.account.trim() || !accForm.category) return;
    if (accForm.category === 'Individual Payment') {
      const ok = await swal.confirm({
        title: `Create "${accForm.account.trim()}" as an Individual Payment?`,
        html: `Every student will be required to hold a balance under this account, and the system will create a linked balance table.<br/><br/>` +
              `<small>Once you save, this cannot be deleted while it has any student balances.</small>`,
        confirmText: 'Yes, create',
        tone: 'primary',
      });
      if (!ok) return;
    }
    setAccSaving(true);
    try {
      const res = await API.post('/account-setup', accForm);
      setAccForm({ account: '', category: accForm.category });   // keep category for serial entry
      swal.success(`"${res.data?.account || accForm.account}" added — add another or change category`);
      await loadAccounts();
    } catch (e) { swal.error(swal.pickError(e, 'Save failed')); }
    finally { setAccSaving(false); }
  };

  const removeAccount = async (a) => {
    const ok = await swal.confirm({
      title: `Delete account "${a.account}"?`,
      text: a.category === 'Individual Payment'
        ? 'The linked balance table will also be dropped. Records can only be removed if no student balances exist.'
        : 'This will remove the payment account from the system.',
      confirmText: 'Yes, delete',
      tone: 'danger',
      icon: 'warning',
    });
    if (!ok) return;
    try {
      await API.delete(`/account-setup/${a.source}/${encodeURIComponent(a.account)}`);
      swal.success(`"${a.account}" deleted`);
      await loadAccounts();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      await API.post('/setup/update', form);
      await refreshSchoolName();
      swal.success('School information saved');
    } catch (e) { swal.error(swal.pickError(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const savePrefix = async () => {
    setPrefixSaving(true);
    try {
      const res = await API.put('/setup/prefix', { prefix });
      setPrefix(res.data?.prefix || prefix);
      swal.success(`Student ID prefix set to "${res.data?.prefix || prefix}"`);
    } catch (e) { swal.error(swal.pickError(e, 'Save failed')); }
    finally { setPrefixSaving(false); }
  };

  const handleLogo = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { swal.warning('Please select an image'); return; }
    if (file.size > 2 * 1024 * 1024)     { swal.warning('Logo must be under 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setLogoSaving(true);
      try {
        await API.post('/setup/logo', { logo: reader.result });
        setLogo(reader.result);
        swal.success('Logo updated');
      } catch (e) { swal.error(swal.pickError(e, 'Upload failed')); }
      finally { setLogoSaving(false); }
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    const ok = await swal.confirm({
      title: 'Remove school logo?',
      text: 'The logo will be removed from printed reports until you upload a new one.',
      confirmText: 'Yes, remove', tone: 'danger', icon: 'warning',
    });
    if (!ok) return;
    setLogoSaving(true);
    try {
      await API.delete('/setup/logo');
      setLogo('');
      swal.success('Logo removed');
    } catch (e) { swal.error(swal.pickError(e, 'Remove failed')); }
    finally { setLogoSaving(false); }
  };

  if (!info) {
    return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      {/* Header */}
      <Card sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', p: { xs: 2.5, md: 3 },
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 52, height: 52 }}>
            <SettingsIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="h5" fontWeight={800}>Settings</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Manage every part of your school system from one place
            </Typography>
          </Box>
          <Chip label={CATEGORIES.find(c => c.key === active)?.label}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }} />
        </Box>
      </Card>

      {/* Top category tabs — frees the entire row below for content */}
      <Card sx={{ mb: 3, position: 'sticky', top: 0, zIndex: 5 }}>
        <Tabs
          value={active}
          onChange={(_, v) => setActive(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            '& .MuiTab-root': {
              minHeight: 60, fontWeight: 700, textTransform: 'none',
              fontSize: 14, letterSpacing: 0.2,
            },
            '& .MuiTabs-indicator': { height: 3 },
          }}
        >
          {CATEGORIES.map((c) => (
            <Tab
              key={c.key}
              value={c.key}
              icon={c.icon}
              iconPosition="start"
              label={c.label}
            />
          ))}
        </Tabs>
      </Card>

      {/* Full-width content */}
      <Box>
          {active === 'general' && (
            <GeneralSettings
              form={form} setForm={setForm} saveDetails={saveDetails} saving={saving}
              logo={logo} setLogo={setLogo} logoSaving={logoSaving}
              fileRef={fileRef} handleLogo={handleLogo} removeLogo={removeLogo} />
          )}
          {active === 'registration' && (
            <RegistrationSettings
              prefix={prefix} setPrefix={setPrefix}
              savePrefix={savePrefix} prefixSaving={prefixSaving} />
          )}
          {active === 'academics' && (
            <>
              <SectionTitle icon={<AcademicsIcon />} title="Academics"
                subtitle="The school week, academic year, grading scheme and exam configuration" />
              {/* The school week is the first thing here because it is
                  the one academic setting the rest of the system already
                  enforces every day — collections, the register and score
                  entry all ask it before they let anyone work. */}
              <SchoolWeekCard />
              <Box sx={{ mt: 2.5 }}>
                <ComingSoon icon={<AcademicsIcon sx={{ fontSize: 32 }} />}
                  title="More academic settings coming soon"
                  hint="Grading bands, exam types and the academic year itself will be configured here. Terms currently live under Finance → Accounting Periods · Terms." />
              </Box>
            </>
          )}
          {active === 'finance' && (
            <>
              <SectionTitle icon={<FinanceIcon />} title="Finance"
                subtitle="Payment accounts, fee items and accounting setup" />

              {/* Inner sub-navigation — scales as more Finance setups are added */}
              <Card sx={{ mb: 2.5 }}>
                <Tabs value={financeTab} onChange={(_, v) => setFinanceTab(v)}
                  variant="scrollable" scrollButtons="auto"
                  sx={{ px: 1, '& .MuiTab-root': { minHeight: 52, fontWeight: 700, textTransform: 'none' } }}>
                  <Tab value="accounts" icon={<AccountSetupIcon fontSize="small" />} iconPosition="start"
                    label="Account Setup / Income Type" />
                  <Tab value="bank" icon={<BankIcon fontSize="small" />} iconPosition="start"
                    label="Bank / Cash · Brought Forward" />
                  <Tab value="expenditures" icon={<ExpendituresIcon fontSize="small" />} iconPosition="start"
                    label="Expenditures · Categories & Types" />
                  <Tab value="periods" icon={<PeriodsIcon fontSize="small" />} iconPosition="start"
                    label="Accounting Periods · Terms" />
                  <Tab value="cashbook_approvals" icon={<BookIcon fontSize="small" />} iconPosition="start"
                    label="Cashbook · Expenditure Approval" />
                  {/* Future Finance settings tabs will be added here */}
                </Tabs>
              </Card>

              {financeTab === 'accounts' && (
                <AccountSetupCard
                  accounts={accounts}
                  accLoading={accLoading}
                  accForm={accForm}
                  setAccForm={setAccForm}
                  accSaving={accSaving}
                  saveAccount={saveAccount}
                  removeAccount={removeAccount}
                  accSearch={accSearch}
                  setAccSearch={setAccSearch}
                />
              )}
              {financeTab === 'bank' && <BankAccountsCard />}
              {financeTab === 'expenditures' && <ExpendituresCard />}
              {financeTab === 'periods' && <AccountingPeriodsCard />}
              {financeTab === 'cashbook_approvals' && <CashbookApprovalCard />}
            </>
          )}
          {active === 'hr' && (
            <>
              <SectionTitle icon={<HrIcon />} title="HR"
                subtitle="Staff management, payroll and leave settings" />
              <ComingSoon icon={<HrIcon sx={{ fontSize: 32 }} />}
                title="HR settings coming soon"
                hint="Designations, pay scales, allowances, deductions and leave types will live here." />
            </>
          )}
      </Box>
    </Box>
  );
}
